"use client";
import React, { useEffect, useRef, useState } from "react";

/**
 * Creates invisible divs that match the exact height/position of SSR list items
 * Uses IntersectionObserver to track which items are visible
 * Updates parent with visible item IDs
 */
export default function InvisibleScrollTracker({
  items = [],
  onVisibleItemsChange,
  isMobile = false,
}) {
  const lastVisibleRef = useRef([]);
  const containerRef = useRef(null);
  const [itemHeights, setItemHeights] = useState([]);
  const [listOffset, setListOffset] = useState(0);

  // Measure actual SSR item heights and positions
  useEffect(() => {
    if (isMobile || items.length === 0) return;

    const measureHeights = () => {
      // Find the parent container with the list
      const listContainer = document.querySelector(".scroller");
      const ssrItems = document.querySelectorAll(".ranking_wrapper");

      if (listContainer && ssrItems.length > 0) {
        const heights = Array.from(ssrItems).map((el) => el.offsetHeight);
        setItemHeights(heights);

        // Calculate offset from top of Grid.Column to start of .scroller
        setListOffset(listContainer.offsetTop);
      }
    };

    // Measure after SSR content renders
    setTimeout(measureHeights, 300);

    // Re-measure on window resize
    window.addEventListener("resize", measureHeights);
    return () => window.removeEventListener("resize", measureHeights);
  }, [items, isMobile]);

  useEffect(() => {
    if (
      isMobile ||
      items.length === 0 ||
      itemHeights.length === 0 ||
      listOffset === 0
    )
      return;

    const visibleItemIds = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const itemId = entry.target.getAttribute("data-item-id");

          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            visibleItemIds.add(itemId);
          } else {
            visibleItemIds.delete(itemId);
          }
        });

        if (visibleItemIds.size >= 3) {
          const visible = items.filter((it) =>
            visibleItemIds.has(String(it.id)),
          );
          const newVisibleItems = visible.slice(0, 3);

          // Only update if changed
          const lastIds = lastVisibleRef.current.map((i) => i.id);
          const newIds = newVisibleItems.map((i) => i.id);

          if (JSON.stringify(lastIds) !== JSON.stringify(newIds)) {
            lastVisibleRef.current = newVisibleItems;
            if (onVisibleItemsChange) {
              onVisibleItemsChange(newVisibleItems);
            }
          }
        }
      },
      { threshold: 0.6 },
    );

    // Wait for invisible trackers to render, then observe them
    setTimeout(() => {
      const trackers = containerRef.current?.querySelectorAll(
        "[data-scroll-tracker]",
      );
      trackers?.forEach((el) => observer.observe(el));
    }, 500);

    return () => observer.disconnect();
  }, [items, isMobile, onVisibleItemsChange, itemHeights, listOffset]);

  if (isMobile || items.length === 0) return null;

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        top: `${listOffset}px`,
        left: 0,
        width: "100%",
        pointerEvents: "none",
        zIndex: -1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {items.map((item, index) => {
        // Use measured height or fallback to 450px
        const height = itemHeights[index] || 450;

        return (
          <div
            key={`tracker-${item.id}`}
            data-scroll-tracker
            data-item-id={item.id}
            className="ranking_wrapper"
            style={{
              height: `${height}px`,
              opacity: 0,
              visibility: "hidden",
            }}
          />
        );
      })}
    </div>
  );
}
